import { FC, useEffect } from 'react';
import { DEFAULT_LOCALE, Locale, useLang } from './Metronici18n';
import { IntlProvider, useIntl } from 'react-intl';
import { setActiveIntl } from './intlInstance';

import deMessages from './messages/de.json';
import enMessages from './messages/en.json';
import frMessages from './messages/fr.json';
import { WithChildren } from '../helpers';

// German is the source of truth and the only set guaranteed to be complete, so
// it is spread underneath every other locale. react-intl renders a message's
// raw key id when the key is absent and the call site passed no
// defaultMessage - and most call sites here pass none. Layering over German
// means an untranslated key degrades to German copy instead of leaking
// "ORDERS.CURRENT.TRACKING.PACKED.TITLE" into the page, which lets EN and FR
// ship incrementally.
const allMessages: Record<Locale, Record<string, string>> = {
  de: deMessages,
  en: { ...deMessages, ...enMessages },
  fr: { ...deMessages, ...frMessages },
};

/**
 * Publishes the active intl for the consumers that run outside React — the
 * redux-saga generators that build toast copy. Rendered inside IntlProvider so it
 * reads the assembled shape rather than rebuilding it.
 */
const PublishIntl = () => {
  const intl = useIntl();
  setActiveIntl(intl);
  return null;
};

const I18nProvider: FC<WithChildren> = ({ children }) => {
  const locale = useLang();
  const messages = allMessages[locale];

  // Keeps the document's language in step with the copy on screen. `index.html`
  // can only carry a static value, which is correct for the default locale and
  // wrong for the other two. Assistive technology reads this attribute to pick
  // pronunciation rules, so French text under `lang="de"` is spoken with German
  // phonetics — wrong in a way nothing on screen reveals.
  //
  // Lives here rather than next to the config in Metronici18n because this
  // component is what decides the locale the tree renders in; the DOM attribute is
  // that same decision, expressed for consumers outside React.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={messages}
      timeZone="Europe/Berlin">
      <PublishIntl />
      {children}
    </IntlProvider>
  );
};

export { I18nProvider };
